import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, BookOpen } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroStatTile } from '@/components/shared/PageHero';
import SalaryTable from '@/components/shared/SalaryTable';
import AffiliateCta from '@/components/shared/AffiliateCta';
import { supabase } from '@/integrations/supabase/client';
import {
  FINANCE_SOC_MAJORS,
  getHighestPayingHub,
  TRADE_SOC_MAJORS,
  type HighestPayingHubConfig,
} from '@/lib/highestPayingHubs';
import { createOccupationSlug } from '@/lib/occupationUtils';
import {
  generateFAQPageSchema,
  generateMultipleSchemas,
  generateOrganizationSchema,
  generateWebPageSchema,
} from '@/lib/schema';

function filterOccupations(
  hub: HighestPayingHubConfig,
  occupations: Array<Record<string, unknown>>
) {
  let rows = [...occupations];

  if (hub.slug === 'trade-jobs') {
    rows = rows.filter((o) => TRADE_SOC_MAJORS.has(String(o.soc || '').slice(0, 2)));
  } else if (hub.slug === 'finance-jobs') {
    rows = rows.filter((o) => FINANCE_SOC_MAJORS.has(String(o.soc || '').slice(0, 2)));
  } else if (hub.socMajorPrefix) {
    rows = rows.filter((o) => String(o.soc || '').startsWith(hub.socMajorPrefix!));
  }

  if (hub.minMeanSalary) {
    rows = rows.filter((o) => Number(o.annual_mean_wage || 0) >= hub.minMeanSalary!);
  }

  rows.sort((a, b) => Number(b.annual_mean_wage || 0) - Number(a.annual_mean_wage || 0));
  return rows.slice(0, hub.limit || 25);
}

const HighestPayingHub = () => {
  const { slug } = useParams<{ slug: string }>();
  const hub = getHighestPayingHub(slug);
  const shouldFetch = !!hub && !hub.existingRoute;

  const { data, isLoading } = useQuery({
    queryKey: ['highest-paying-hub', hub?.slug, hub?.apiType],
    enabled: shouldFetch,
    queryFn: async () => {
      const { data: payload, error } = await supabase.functions.invoke('api-top-occupations', {
        body: {
          area: 'United States',
          type: hub!.apiType,
          limit: 100,
          rpp: false,
        },
      });
      if (error) throw error;
      return payload;
    },
  });

  const occupations = useMemo(
    () => (hub && shouldFetch ? filterOccupations(hub, data?.occupations || []) : []),
    [data, hub, shouldFetch]
  );

  if (!hub) {
    return <Navigate to="/rankings/" replace />;
  }

  if (hub.existingRoute) {
    return <Navigate to={hub.existingRoute} replace />;
  }

  const year = new Date().getFullYear();
  const pageTitle = `${hub.title} (${year}) | Wage Atlas`;
  const pageDescription = hub.description;
  const canonicalUrl = `https://wageatlas.com/highest-paying/${hub.slug}/`;

  const topSalary = occupations[0]?.annual_mean_wage
    ? Number(occupations[0].annual_mean_wage)
    : 0;
  const avgSalary =
    occupations.length > 0
      ? Math.round(
          occupations.reduce((sum, o) => sum + Number(o.annual_mean_wage || 0), 0) /
            occupations.length
        )
      : 0;

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);

  const faqSchema = generateFAQPageSchema([
    {
      question: `What are the ${hub.title.toLowerCase()}?`,
      answer: hub.intro,
    },
    {
      question: 'Where does this ranking data come from?',
      answer: hub.methodology,
    },
    {
      question: 'Does this include bonuses and equity?',
      answer:
        'BLS OEWS figures are primarily wages and salaries reported by employers. Equity, most bonuses, and overtime are incompletely captured, so total compensation in tech and finance can exceed these figures.',
    },
  ]);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: hub.title,
    itemListOrder: 'https://schema.org/ItemListOrderDescending',
    numberOfItems: occupations.length,
    itemListElement: occupations.slice(0, 20).map((occ, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: String(occ.occ_title || ''),
      url: `https://wageatlas.com/salary/${createOccupationSlug(String(occ.occ_title || ''))}/united-states/`,
    })),
  };

  const schemas = generateMultipleSchemas([
    generateOrganizationSchema(),
    generateWebPageSchema(pageTitle, pageDescription, canonicalUrl),
    faqSchema,
    itemListSchema,
  ]);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        schemas={schemas}
      />

      <PageHero
        badge={{ icon: TrendingUp, label: 'OEWS Ranked' }}
        title={hub.title}
        subtitle={<>{hub.intro}</>}
      >
        {!isLoading && occupations.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
            <HeroStatTile
              icon={DollarSign}
              value={formatCurrency(topSalary)}
              label="Top mean wage"
            />
            <HeroStatTile
              icon={DollarSign}
              value={formatCurrency(avgSalary)}
              label="List average"
            />
            <HeroStatTile
              icon={BookOpen}
              value={`${occupations.length}`}
              label="Occupations ranked"
            />
          </div>
        )}
      </PageHero>

      <div className="container mx-auto px-4 py-8 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">National ranking</CardTitle>
            <p className="text-sm text-muted-foreground">
              Click any occupation for state-level salaries, percentiles, and real (cost-of-living
              adjusted) pay.
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                <div className="h-10 bg-muted animate-pulse rounded" />
                <div className="h-64 bg-muted animate-pulse rounded" />
              </div>
            ) : occupations.length > 0 ? (
              <SalaryTable
                firstColumnLabel="Occupation"
                data={occupations.map((occ, index) => ({
                  rank: index + 1,
                  occ_title: String(occ.occ_title || 'Unknown'),
                  soc: String(occ.soc || ''),
                  annual_mean_wage: Number(occ.annual_mean_wage || 0),
                  annual_median_wage: Number(occ.annual_median_wage || 0),
                  hourly_mean_wage: Number(occ.hourly_mean_wage || 0),
                  total_employment: Number(occ.total_employment || 0),
                  mean_salary: Number(occ.annual_mean_wage || 0),
                  median_salary: Number(occ.annual_median_wage || 0),
                  hourly_mean: Number(occ.hourly_mean_wage || 0),
                  tot_emp: Number(occ.total_employment || 0),
                }))}
                area="United States"
                showAffiliateEvery={8}
                className="shadow-none border-0"
              />
            ) : (
              <p className="text-muted-foreground py-8 text-center">
                No occupations matched this filter in the latest OEWS release.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Methodology</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>{hub.methodology}</p>
            <p>
              Full documentation:{' '}
              <Link to="/methodology/" className="text-primary hover:underline">
                Wage Atlas methodology
              </Link>{' '}
              and{' '}
              <Link to="/data-sources/" className="text-primary hover:underline">
                data sources
              </Link>
              .
            </p>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3 items-center">
          <Badge variant="outline">BLS OEWS</Badge>
          <Badge variant="outline">National ranking</Badge>
          <Link to="/no-degree-jobs/" className="text-sm text-primary hover:underline">
            No-degree jobs
          </Link>
          <Link to="/best-paying-entry-level/" className="text-sm text-primary hover:underline">
            Entry-level
          </Link>
          <Link to="/ai-economy-jobs/" className="text-sm text-primary hover:underline">
            AI economy
          </Link>
          <Link to="/occupations/" className="text-sm text-primary hover:underline">
            All occupations
          </Link>
        </div>

        <AffiliateCta provider="coursera" type="training" />
      </div>
    </div>
  );
};

export default HighestPayingHub;
